import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Eye, Users, ChevronLeft, ChevronRight, XCircle } from 'lucide-react';
import { useRegistrations } from '../hooks/useRegistrations';
import RegistrationDetail from './RegistrationDetail';
import CenterFilter from './CenterFilter';

const statusStyles = {
  not_registered: 'bg-slate-100 text-slate-700',
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

export default function RegistrationList() {
  const {
    registrations,
    loading,
    error,
    page,
    setPage,
    total,
    pageSize,
    status,
    setStatus,
    centerId,
    setCenterId,
    refresh,
  } = useRegistrations();
  const [selectedId, setSelectedId] = useState(null);

  const totalPages = Math.ceil(total / pageSize) || 1;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Registration Requests</h1>
        <p className="text-gray-600 mt-1">Approve each child and assign a course — fee is set automatically</p>
      </div>

      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex gap-2 flex-wrap">
        {[
          ['pending', 'Pending'],
          ['approved', 'Approved'],
          ['rejected', 'Rejected'],
          ['not_registered', 'Not Registered'],
        ].map(([value, label]) => (
          <button
            key={value}
            onClick={() => {
              setPage(0);
              setStatus(value);
            }}
            className={`px-4 py-2 rounded-lg font-medium transition-all ${
              status === value
                ? 'bg-blue-500 text-white'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {label}
          </button>
        ))}
        </div>
        <CenterFilter
          value={centerId}
          onChange={(id) => {
            setPage(0);
            setCenterId(id);
          }}
        />
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-800 flex items-center gap-3">
          <XCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
        </div>
      ) : (registrations ?? []).length > 0 ? (
        <div className="space-y-3">
          {registrations.map((registration) => (
            <div
              key={registration.id}
              className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="font-semibold text-gray-900">{registration.child_name || registration.full_name}</h3>
                    <span className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${statusStyles[registration.registration_status] || statusStyles.not_registered}`}>
                      {registration.registration_status.replace('_', ' ')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">
                    Parent: {registration.full_name} · {registration.phone_number || 'No phone'}
                    {registration.center_name ? ` · ${registration.center_name}` : ''}
                    {registration.child_grade ? ` · ${registration.child_grade}` : ''}
                  </p>
                  {registration.course_name && (
                    <p className="text-sm text-green-700 mt-1">
                      {registration.course_name} — {registration.assigned_fee} Birr
                    </p>
                  )}
                  <p className="text-xs text-gray-500 mt-2">
                    Submitted {formatDistanceToNow(new Date(registration.created_at), { addSuffix: true })}
                  </p>
                </div>
                <button
                  onClick={() => setSelectedId(registration.id)}
                  className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  title="Review registration"
                >
                  <Eye className="w-5 h-5" />
                </button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200 text-gray-500">
          No registrations in this category
        </div>
      )}

      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <button
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4" />
            Previous
          </button>
          <span className="text-sm text-gray-600">
            Page {page + 1} of {totalPages} ({total} total)
          </span>
          <button
            onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
            disabled={page >= totalPages - 1}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg disabled:opacity-50"
          >
            Next
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {selectedId && (
        <RegistrationDetail
          registrationId={selectedId}
          onClose={() => setSelectedId(null)}
          onUpdated={refresh}
        />
      )}
    </div>
  );
}
